import * as React from 'react';

import classNames from 'classnames';
import InputWithIcon, { InputWithIconProps } from '../InputWithIcon';

type MaskMode = 'landline' | 'cellphone';

const getMaskMode = (phone?: string): MaskMode => {
  const onlyNumbers = phone?.match(/[0-9]/g)?.join('');
  if (!onlyNumbers) return 'cellphone';
  if (onlyNumbers.length < 3) {
    return 'cellphone';
  }
  return onlyNumbers.length > 3 && onlyNumbers[2] === '9'
    ? 'cellphone'
    : 'landline';
};

const formatPhone = (phone: string, mode: MaskMode): string => {
  const onlyNumbers = phone.replace(/\D/g, '');
  if (!onlyNumbers) return '';

  if (mode === 'landline') {
    // Format as (99) 9999-9999
    const part1 = onlyNumbers.slice(0, 2);
    const part2 = onlyNumbers.slice(2, 6);
    const part3 = onlyNumbers.slice(6, 10);
    let formatted = '';
    if (part1) formatted += `(${part1})`;
    if (part2) formatted += ` ${part2}`;
    if (part3) formatted += `-${part3}`;
    return formatted;
  }
  // Format as (99) 99999-9999
  const part1 = onlyNumbers.slice(0, 2);
  const part2 = onlyNumbers.slice(2, 7);
  const part3 = onlyNumbers.slice(7, 11);
  let formatted = '';
  if (part1) formatted += `(${part1})`;
  if (part2) formatted += ` ${part2}`;
  if (part3) formatted += `-${part3}`;
  return formatted;
};

export type Props = Omit<InputWithIconProps, 'value' | 'onChange'> & {
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const PhoneInput: React.FC<Props> = ({
  value = '',
  onChange,
  className,
  ...props
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [mode, setMode] = React.useState<MaskMode>(getMaskMode(value));
  const [formattedValue, setFormattedValue] = React.useState<string>(
    formatPhone(value, mode),
  );

  React.useEffect(() => {
    const newMode = getMaskMode(value);
    setMode(newMode);
    setFormattedValue(formatPhone(value, newMode));
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectionStart = event.target.selectionStart ?? 0;
    const beforeCursor = event.target.value.slice(0, selectionStart);
    const digitsBeforeCursor = beforeCursor.replace(/[^\d]/g, '').length;

    const inputValue = event.target.value;
    const newMode = getMaskMode(inputValue);
    setMode(newMode);
    const newFormattedValue = formatPhone(inputValue, newMode);
    setFormattedValue(newFormattedValue);

    window.requestAnimationFrame(() => {
      const inputEl = inputRef.current;
      if (!inputEl) return;

      let digitCount = 0;
      let newCursor = 0;

      for (let i = 0; i < newFormattedValue.length; i += 1) {
        if (/\d/.test(newFormattedValue[i])) {
          digitCount += 1;
        }
        if (digitCount === digitsBeforeCursor) {
          newCursor = i + 1;
          break;
        }
      }

      inputEl.setSelectionRange(newCursor, newCursor);
    });

    if (onChange) {
      // Create a synthetic event with formatted value
      const syntheticEvent = {
        ...event,
        target: {
          ...event.target,
          value: newFormattedValue,
        },
      };
      onChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <InputWithIcon
      ref={inputRef}
      className={classNames('d-flex flex-column', className)}
      value={formattedValue}
      onChange={handleChange}
      {...props}
    />
  );
};

export default PhoneInput;
